import { Button, Checkbox, FileDrop, Icon, Table, Tooltip } from '@scuf/common';
import useFetch from 'helpers/hooks/useFetch';
import i18n from 'i18n';
import { DistributionPreviewResponse, DistributionPreviewRow } from 'models/repaircenter/rmaModel';
import { useState } from 'react';
import { ReactCountryFlag } from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { REPAIR_CENTER } from 'resources';
import { notificationAtom } from 'resources/atoms/notificationAtom';

function RmaDistribution() {
    const { t } = useTranslation(['home', 'common', 'repaircenter', 'notification']);
    const setNotification = useSetRecoilState(notificationAtom);

    const [arquivoXLSX, setArquivoXLSX] = useState<File | undefined>();
    const [previewRows, setPreviewRows] = useState<DistributionPreviewRow[] | null>(null);
    const [warnings, setWarnings] = useState<number | undefined>(undefined);
    const [fileDropKey, setFileDropKey] = useState(0);
    const [isUploaded, setIsUploaded] = useState(false);
    const [dateFormat, setDateFormat] = useState<'US' | 'BR' | null>(null);


    const [processedFile, postUploadFile, loadingPreview] = useFetch<
        { fileName: string; fileContent: string; dateFormat: string | null; },
        { error: boolean; message: string; data: DistributionPreviewResponse }
    >({
        url: REPAIR_CENTER.generatePreviewDistribution,
        method: 'POST',
        options: {
            headers: { 'Content-Type': 'application/json' }
        },
        onError: t('notification:critical.Process'),
    });

    const [, savePreviewDistribution, loadingSave] = useFetch<DistributionPreviewRow[], { message: string; }>({
        url: REPAIR_CENTER.savePreviewDistribution,
        method: 'POST',
        onError: t('notification:critical.Process'),
    });


    const canGenerate = Boolean(arquivoXLSX)
        && !loadingPreview
        && !loadingSave
        && dateFormat !== null;

    const canSave = Boolean(previewRows?.length) && !loadingSave && !loadingPreview;

    const handleClickGenerate = async () => {
        if (!arquivoXLSX) {
            setNotification(old => ({ ...old, type: 'information', message: t('common:translation.SelectAFileFirst') }));
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            if (!e.target) return;

            const base64String = (e.target.result as string)
                .replace('data:', '')
                .replace(/^.+,/, '');

            setPreviewRows(null);
            setWarnings(undefined);

            await postUploadFile(
                { fileName: arquivoXLSX.name, fileContent: base64String, dateFormat: dateFormat },
                (response) => {
                    const { rows: rows, warnings: warnings } = (response as any)?.data as DistributionPreviewResponse ?? {};

                    if (!rows.length) {
                        setNotification(old => ({ ...old, type: 'information', message: t('common:translation.NoData') }));
                    } else {
                        setNotification(old => ({ ...old, type: 'success', message: t('repaircenter:translation.PreviewGenerated') }));
                    }

                    setPreviewRows(rows ?? []);
                    setWarnings(warnings);
                }
            );
        };

        reader.readAsDataURL(arquivoXLSX);
    };

    const handleClickSave = async () => {
        if (!previewRows?.length) return;

        const rowsToSave = previewRows.map(
            ({ serialNumber, partNumber, description, saleDate, warranty, factoryWarranty, customerName, distributionName }) => ({
                serialNumber, partNumber, description, saleDate,
                warranty, factoryWarranty, customerName, distributionName,
            })
        );

        await savePreviewDistribution(rowsToSave, (response) => {
            if (response?.error) {
                setNotification(old => ({ ...old, type: 'critical', message: t('notification:critical.Save') }));
            } else {
                setNotification(old => ({ ...old, type: 'success', message: t('notification:success.Save') }));
                setFileDropKey(k => k + 1);
                setArquivoXLSX(undefined);
                setPreviewRows(null);
                setWarnings(undefined);
            }
        });
    };

    const showPreview = (previewRows?.length ?? 0) > 0;

    function formatDateDescritivo(dateInput: string | undefined, locale: string = 'pt-BR'): string {
        if (!dateInput) return '';

        let day: number, month: number, year: number;

        if (dateInput.includes('/')) {
            [day, month, year] = dateInput.split('/').map(Number);
        } else {
            [year, month, day] = dateInput.split('-').map(Number);
        }

        const date = new Date(year, month - 1, day);
        if (isNaN(date.getTime())) return '';

        const dia = date.getDate();
        const mes = date.toLocaleString(locale, { month: 'long' });
        const ano = date.getFullYear();
        const mesFormatado = mes.charAt(0).toUpperCase() + mes.slice(1);

        const connector = (locale === 'en' || locale === 'en-US') ? 'of' : 'de';
        return `${dia} ${connector} ${mesFormatado} ${connector} ${ano}`.trim();
    }

    return (
        <>
            <div>
                <FileDrop
                    key={fileDropKey}
                    acceptedTypes={['.xlsx']}
                    resetButtonText={t('common:actions.Clean')}
                    multiple={false}
                    buttonText={t('common:actions.BrowseFile')}
                    label={t('common:translation.FileDistribution').toUpperCase()}
                    placeholder={t('common:actions.Draganddropfilehere')}
                    onAccepted={(file: File[]) => {
                        setArquivoXLSX(file[0]);
                        setPreviewRows(null);
                        setWarnings(undefined);
                        setIsUploaded(true);
                    }}
                    onReset={() => {
                        setArquivoXLSX(undefined);
                        setPreviewRows(null);
                        setWarnings(undefined);
                        setIsUploaded(false);
                    }}
                />
                <div style={{ display: 'flex', marginTop: '16px', justifyContent: 'end', columnGap: '20px' }}>
                    {isUploaded && (<div>
                        <div style={{ marginBottom: '6px', fontWeight: 500, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                            {t('common:translation.DateFormat')} <Tooltip element={<Icon name="badge-info" size="small" />} content={t('common:translation.DateMessage')} position="top center" />
                        </div>
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                <input
                                    type="checkbox"
                                    checked={dateFormat === 'US'}
                                    onChange={(e) => setDateFormat(e.target.checked ? 'US' : null)}
                                />
                                <ReactCountryFlag
                                    countryCode="US"
                                    svg
                                    style={{ width: '20px', height: '20px' }}
                                />
                                US (MM/dd/yyyy)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                <input
                                    type="checkbox"
                                    checked={dateFormat === 'BR'}
                                    onChange={(e) => setDateFormat(e.target.checked ? 'BR' : null)}
                                />
                                <ReactCountryFlag
                                    countryCode="BR"
                                    svg
                                    style={{ width: '20px', height: '20px' }}
                                />
                                BR (dd/MM/yyyy)
                            </label>
                        </div>
                    </div>)}
                    <Button
                        type="primary"
                        disabled={!canGenerate}
                        content={t('common:actions.GeneratePreview')}
                        onClick={handleClickGenerate}
                        loading={loadingPreview}
                    />

                </div>
            </div>

            {showPreview && (
                <div>
                    <h4>{t('common:translation.FileData')}</h4>
                    {warnings !== undefined ? (<h5>{warnings} {t("repaircenter:translation.SNorPNWarnings")}</h5>) : null}
                    <Table>
                        <Table.Header>
                            <Table.HeaderCell content={t('common:translation.SerialNumber')} />
                            <Table.HeaderCell content={t('common:translation.PartNumber')} />
                            <Table.HeaderCell content={t('common:translation.Description')} />
                            <Table.HeaderCell content={t('repaircenter:translation.SaleDate')} />
                            <Table.HeaderCell content={t('repaircenter:translation.Warranty')} />
                            <Table.HeaderCell content={t('common:translation.FactoryWarranty')} />
                            <Table.HeaderCell content={t('common:translation.Customer')} />
                            <Table.HeaderCell content={t('common:translation.DistributionName')} />
                        </Table.Header>
                        <Table.Body>
                            {previewRows!.map((row, idx) => (
                                <Table.Row key={`${row.serialNumber}-${row.partNumber}-${idx}`}>
                                    <Table.Cell content={row.serialNumber} />
                                    <Table.Cell content={row.partNumber} />
                                    <Table.Cell content={row.description} />
                                    <Table.Cell content={formatDateDescritivo(row.saleDate, i18n.language)} />
                                    <Table.Cell content={row.warranty} />
                                    <Table.Cell content={formatDateDescritivo(row.factoryWarranty, i18n.language)} />
                                    <Table.Cell content={row.customerName} />
                                    <Table.Cell content={row.distributionName} />
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                    <div style={{ display: 'flex', justifyContent: 'end' }}>
                        <Button
                            type="primary"
                            disabled={!canSave}
                            content={t('common:actions.Save')}
                            onClick={handleClickSave}
                            loading={loadingSave}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default RmaDistribution;